import {
  I2cRadioButton,
  I2cCheckbox,
  I2cCheckboxGroup,
  I2cRadioGroup,
  I2cListItem,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { validationSchemaOnlineCardSales } from "./ValidationSchema"; 
import { agentWebsiteLang, webRequire } from "./stringUtils";

const OnlineCardsSales = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaOnlineCardSales),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      online_card_sales_would_you_like_i2c_c_cart: "no",
      online_card_sales_i2c_c_card_language: [],
      online_card_sales_i2c_c_card_language_other: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "online_card_sales_would_you_like_i2c_c_cart",
    "online_card_sales_i2c_c_card_language",
    "online_card_sales_i2c_c_card_language_other",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      3: {
        ...addFormData["3"],
        "3_2": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[3]?.["3_2"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("3_2", true);
    } else {
      setFormValidStates("3_2", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isWebRequired = methods.watch("online_card_sales_would_you_like_i2c_c_cart") === "yes";

  // Handle checkbox group change
  const handleWebLangAdd = (lang) => {
    let updated = [...methods.getValues("online_card_sales_i2c_c_card_language")];
    if (updated.includes(lang)) {
      updated = updated.filter((l) => l !== lang);
      if (lang === "other") {
        methods.setValue("online_card_sales_i2c_c_card_language_other", "");
      }
    } else {
      updated.push(lang);
    }
    methods.setValue("online_card_sales_i2c_c_card_language", updated);
    methods.trigger();
    handleValueChanges();
  };

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="sub-section">
              <div className="sub-heading mb-16">
                Would you like i2c C-Cart?
              </div>
              <I2cRadioGroup
                label=""
                value={methods.watch(
                  "online_card_sales_would_you_like_i2c_c_cart",
                )}
                name="online_card_sales_would_you_like_i2c_c_cart"
                onI2cChange={(e) => {
                  const value = String(e.target.value);
                  methods.setValue(
                    "online_card_sales_would_you_like_i2c_c_cart",
                    value,
                  );

                  if (value !== "yes") {
                    methods.setValue(
                      "online_card_sales_i2c_c_card_language",
                      [],
                    );
                    methods.setValue(
                      "online_card_sales_i2c_c_card_language_other",
                      "",
                    );
                  }

                  methods.trigger();
                  handleValueChanges();
                }}
              >
                {webRequire.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.online_card_sales_would_you_like_i2c_c_cart && (
                <ErrorMessage
                  message={
                    errors.online_card_sales_would_you_like_i2c_c_cart.message
                  }
                />
              )}
            </div>
            {isWebRequired && (
              <>
                <div className="sub-section">
                  <div className="sub-heading mb-16">C-Cart Language</div>
                  <I2cCheckboxGroup
                    key={`c-cart-language-${methods.watch("online_card_sales_would_you_like_i2c_c_cart")}`}
                  >
                    <div i2c-grid="grid gap-sm">
                      {agentWebsiteLang.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods
                            .watch("online_card_sales_i2c_c_card_language")
                            .includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleWebLangAdd(web.value)
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="ic_cAgentLang"
                            value={web.value}
                            label=""
                            filled
                            checked={methods
                              .watch("online_card_sales_i2c_c_card_language")
                              .includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.online_card_sales_i2c_c_card_language && (
                    <ErrorMessage
                      message={
                        errors.online_card_sales_i2c_c_card_language.message
                      }
                    />
                  )}
                  {methods
                    .watch("online_card_sales_i2c_c_card_language")
                    .includes("other") && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="online_card_sales_i2c_c_card_language_other"
                            value={methods.watch(
                              "online_card_sales_i2c_c_card_language_other",
                            )}
                            onI2cInput={(e) => {
                              const value = e.target.value;
                              methods.setValue(
                                "online_card_sales_i2c_c_card_language_other",
                                value,
                              );
                              methods.trigger();
                              handleValueChanges();
                            }}
                            invalid={
                              errors.online_card_sales_i2c_c_card_language_other
                                ? true
                                : false
                            }
                          />
                          {errors.online_card_sales_i2c_c_card_language_other && (
                            <ErrorMessage
                              message={
                                errors
                                  .online_card_sales_i2c_c_card_language_other
                                  .message
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default OnlineCardsSales;
